

import { useState, useMemo } from "react";
import { format, parseISO, startOfHour, startOfDay, isWithinInterval, subDays, subHours } from "date-fns";

type ChartFilter = "24h" | "7d" | "30d" | "lifetime";

interface InstallEvent {
  // firestore Timestamp or ISO string or number
  timestamp?: any;
  cpi: number;
  eventCount: number;
  date: string;
}

interface DataPoint {
  label: string;
  timestamp: Date;
  installs: number;
  earnings: number;
}

interface Props {
  events: InstallEvent[];
}

export function EarningsChart({ events }: Props) {
  const [filter, setFilter] = useState<ChartFilter>("lifetime");
  const [hoveredPoint, setHoveredPoint] = useState<DataPoint | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<DataPoint | null>(null);

  const chartData = useMemo(() => {
    console.log('[EarningsChart] Computing chartData, filter:', filter, 'events count:', events.length);
    const now = new Date();
    let startDate: Date;
    let groupByHour = false;

    switch (filter) {
      case "24h":
        startDate = subHours(now, 24);
        groupByHour = true;
        break;
      case "7d":
        startDate = subDays(now, 7);
        break;
      case "30d":
        startDate = subDays(now, 30);
        break;
      case "lifetime":
        // For lifetime, determine startDate from earliest event and end (now) from latest event.
        // We'll compute min/max from the incoming events below. Temporarily set startDate to a very old date;
        // it will be replaced when we compute the baseline buckets if events exist.
        startDate = subDays(now, 3650);
        break;
    }

    // Create baseline data points even if no events
    const baseline = new Map<string, DataPoint>();
    if (groupByHour) {
      // Create 24 hourly buckets
      for (let i = 0; i < 24; i++) {
        const timestamp = subHours(now, 24 - i);
        const hourStart = startOfHour(timestamp);
        const key = hourStart.toISOString();
        baseline.set(key, {
          label: format(hourStart, 'MMM d, h:mm a'),
          timestamp: hourStart,
          installs: 0,
          earnings: 0,
        });
      }
    } else {
      // Create daily buckets. For 'lifetime' we derive the range from the events' min/max dates.
      let days: number;

      if (filter === "7d") {
        days = 7;
        for (let i = 0; i < days; i++) {
          const timestamp = subDays(now, days - 1 - i);
          const dayStart = startOfDay(timestamp);
          const key = dayStart.toISOString();
          baseline.set(key, {
            label: format(dayStart, 'MMM d'),
            timestamp: dayStart,
            installs: 0,
            earnings: 0,
          });
        }
      } else if (filter === "30d") {
        days = 30;
        for (let i = 0; i < days; i++) {
          const timestamp = subDays(now, days - 1 - i);
          const dayStart = startOfDay(timestamp);
          const key = dayStart.toISOString();
          baseline.set(key, {
            label: format(dayStart, 'MMM d'),
            timestamp: dayStart,
            installs: 0,
            earnings: 0,
          });
        }
      } else if (filter === 'lifetime') {
        // For lifetime, compute min and max dates from events (after parsing). If no events, fall back
        // to last 90 days. Cap the total days to 1000 to avoid excessive buckets.
        const parsedDates: Date[] = events.map(e => parseEventDate(e)).filter(d => d !== null) as Date[];
        if (parsedDates.length > 0) {
          const minTs = new Date(Math.min(...parsedDates.map(d => d.getTime())));
          const maxTs = new Date(Math.max(...parsedDates.map(d => d.getTime())));
          // Normalize to startOfDay for buckets
          const startDay = startOfDay(minTs);
          const endDay = startOfDay(maxTs);
          const msPerDay = 24 * 60 * 60 * 1000;
          let totalDays = Math.ceil((endDay.getTime() - startDay.getTime()) / msPerDay) + 1;
          const CAP_DAYS = 1000;
          if (totalDays > CAP_DAYS) totalDays = CAP_DAYS;
          for (let i = 0; i < totalDays; i++) {
            const dayStart = new Date(startDay.getTime() + i * msPerDay);
            const key = dayStart.toISOString();
            baseline.set(key, {
              label: format(dayStart, 'MMM d'),
              timestamp: dayStart,
              installs: 0,
              earnings: 0,
            });
          }
          // Update startDate to min event (used in client-side filtering later)
          startDate = startDay;
        } else {
          // Fallback: last 90 days
          days = 90;
          for (let i = 0; i < days; i++) {
            const timestamp = subDays(now, days - 1 - i);
            const dayStart = startOfDay(timestamp);
            const key = dayStart.toISOString();
            baseline.set(key, {
              label: format(dayStart, 'MMM d'),
              timestamp: dayStart,
              installs: 0,
              earnings: 0,
            });
          }
        }
      } else {
        // Default daily buckets (shouldn't reach here)
        days = 30;
        for (let i = 0; i < days; i++) {
          const timestamp = subDays(now, days - 1 - i);
          const dayStart = startOfDay(timestamp);
          const key = dayStart.toISOString();
          baseline.set(key, {
            label: format(dayStart, 'MMM d'),
            timestamp: dayStart,
            installs: 0,
            earnings: 0,
          });
        }
      }
    }

    // Helper: parse event timestamp that may be a Firestore Timestamp, number, or ISO string
    function parseEventDate(event: InstallEvent): Date | null {
      if (!event) return null;
      // @ts-ignore - Firestore Timestamp has toDate()
      if (event.timestamp && typeof event.timestamp.toDate === 'function') {
        // @ts-ignore
        return event.timestamp.toDate();
      }
      if (typeof event.timestamp === 'number') {
        return new Date(event.timestamp);
      }
      if (typeof event.timestamp === 'string' && event.timestamp.length > 0) {
        try { return parseISO(event.timestamp); } catch { /* ignore */ }
      }
      // support custom format with trailing -HHMM (time in EST e.g. '-1634')
      if (event.date && typeof event.date === 'string') {
        const m = event.date.match(/-(\d{3,4})$/);
        if (m) {
          const hm = m[1];
          const h = hm.length === 3 ? parseInt(hm.slice(0,1), 10) : parseInt(hm.slice(0,2), 10);
          const min = hm.length === 3 ? parseInt(hm.slice(1), 10) : parseInt(hm.slice(2), 10);

          // Determine base date: prefer event.timestamp date if available, else use current date in EST
          let baseYear: number, baseMonth: number, baseDay: number;
          try {
            if (event.timestamp && typeof (event.timestamp as any).toDate === 'function') {
              const d = (event.timestamp as any).toDate();
              baseYear = d.getFullYear(); baseMonth = d.getMonth() + 1; baseDay = d.getDate();
            } else if (typeof event.timestamp === 'number') {
              const d = new Date(event.timestamp);
              baseYear = d.getFullYear(); baseMonth = d.getMonth() + 1; baseDay = d.getDate();
            } else {
              const now = new Date();
              const estNow = new Date(now.getTime() - 5 * 60 * 60 * 1000);
              baseYear = estNow.getFullYear(); baseMonth = estNow.getMonth() + 1; baseDay = estNow.getDate();
            }
          } catch (err) {
            const now = new Date();
            const estNow = new Date(now.getTime() - 5 * 60 * 60 * 1000);
            baseYear = estNow.getFullYear(); baseMonth = estNow.getMonth() + 1; baseDay = estNow.getDate();
          }

          const mm = String(baseMonth).padStart(2, '0');
          const dd = String(baseDay).padStart(2, '0');
          const hh = String(h).padStart(2, '0');
          const mins = String(min).padStart(2, '0');

          const iso = `${baseYear}-${mm}-${dd}T${hh}:${mins}:00-05:00`;
          const parsed = new Date(iso);
          if (!isNaN(parsed.getTime())) return parsed;
        }

        if (event.date.length === 8) {
          try { return parseISO(event.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')); } catch { /* ignore */ }
        }
      }
      return null;
    }

    // Filter events within range
    const filteredEvents = events.filter((event) => {
      try {
        const eventDate = parseEventDate(event);
        if (!eventDate) return false;
        return isWithinInterval(eventDate, { start: startDate, end: now });
      } catch {
        return false;
      }
    });

    // Debug logs to help diagnose empty charts
    console.log('[EarningsChart] Filter applied:', { filter, startDate: startDate.toISOString(), now: now.toISOString() });
    console.log('[EarningsChart] Filtered events count:', filteredEvents.length, 'of', events.length, 'total');
    if (filteredEvents.length > 0 && filteredEvents.length <= 5) {
      console.log('[EarningsChart] Sample filtered events:', filteredEvents.map(e => ({
        // @ts-ignore
        id: (e as any).id,
        date: e.date,
        // @ts-ignore
        timestamp: e.timestamp && typeof e.timestamp.toDate === 'function' ? e.timestamp.toDate().toISOString() : e.timestamp,
        cpi: e.cpi,
        eventCount: e.eventCount,
        earnings: (e.cpi * e.eventCount).toFixed(4)
      })));
    }
    console.log('[EarningsChart] Baseline buckets:', baseline.size);

    // Add event data to baseline
    let mappedCount = 0;
    let unmappedCount = 0;
    filteredEvents.forEach((event) => {
      try {
        const eventDate = parseEventDate(event)!;
        const key = groupByHour 
          ? startOfHour(eventDate).toISOString()
          : startOfDay(eventDate).toISOString();

        // Debug: show mapping from event -> bucket key
        try {
          // @ts-ignore
          const evId = (event as any).id || '(no-id)';
          // @ts-ignore
          const ts = event.timestamp && typeof event.timestamp.toDate === 'function' ? event.timestamp.toDate().toISOString() : event.timestamp;
          console.log('EarningsChart mapping', { evId, eventDate: ts, computedKey: key, hasBucket: baseline.has(key) });
        } catch (dE) {
          console.warn('EarningsChart mapping debug failed', dE);
        }

        if (baseline.has(key)) {
          const point = baseline.get(key)!;
          point.installs += event.eventCount;
          point.earnings += event.cpi * event.eventCount;
          mappedCount++;
        } else {
          unmappedCount++;
        }
      } catch (error) {
        console.error('Error parsing event date:', error, event);
      }
    });

    const result = Array.from(baseline.values()).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const totalChartEarnings = result.reduce((sum, pt) => sum + pt.earnings, 0);
    const totalChartInstalls = result.reduce((sum, pt) => sum + pt.installs, 0);
    console.log('[EarningsChart] Chart computed:', { 
      filter, 
      mappedCount, 
      unmappedCount, 
      bucketsWithData: result.filter(r => r.earnings > 0).length,
      totalChartEarnings: totalChartEarnings.toFixed(4),
      totalChartInstalls
    });
    return result;
  }, [events, filter]);

  // Compute max earnings only from the visible buckets for the active filter.
  // This prevents out-of-range spikes (older than the selected filter) from
  // shrinking the visible bars in the 24h/7d/30d views.
  const maxEarnings = useMemo(() => {
    if (!chartData || chartData.length === 0) return 1;
    let visible: typeof chartData = chartData;
    if (filter === '24h') {
      visible = chartData.slice(-24);
    } else if (filter === '7d') {
      visible = chartData.slice(-7);
    } else if (filter === '30d') {
      visible = chartData.slice(-30);
    } else if (filter === 'lifetime') {
      visible = chartData.slice(-90);
    }
    const vals = visible.map((d) => d.earnings);
    const maxVal = vals.length ? Math.max(...vals) : 0;
    return Math.max(maxVal, 1);
  }, [chartData, filter]);

  const totals = useMemo(() => {
    return chartData.reduce(
      (acc, d) => {
        acc.earnings += d.earnings;
        acc.installs += d.installs;
        return acc;
      },
      { earnings: 0, installs: 0 }
    );
  }, [chartData]);

  // Which point to display in the summary: selected (clicked) > hovered > totals
  const displayedPoint = selectedPoint ?? hoveredPoint ?? null;



  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Analytics Timeline</h3>
        <div className="flex gap-2">
          {(["24h", "7d", "30d", "lifetime"] as ChartFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setSelectedPoint(null); // clear any selection when filter changes
              }}
              className={`rounded-full px-3 py-1 text-xs transition ${
                filter === f
                  ? "bg-lime-400 text-black font-semibold"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Summary panel: shows totals for the filter, or a hovered/selected day */}
      <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-white/5 bg-black/20 p-3">
        <div>
          {(() => {
            const label = displayedPoint
              ? (filter === '24h' ? format(displayedPoint.timestamp, 'MMM d, h:mm a') : format(displayedPoint.timestamp, 'MMM d'))
              : `${filter} totals`;
            const earnings = displayedPoint ? displayedPoint.earnings : totals.earnings;
            const installs = displayedPoint ? displayedPoint.installs : totals.installs;
            return (
              <>
                <p className="text-xs text-white/60">{label}</p>
                <p className="mt-1 text-2xl font-bold text-emerald-300">${earnings.toFixed(2)}</p>
                <p className="text-xs text-white/60">{installs} installs</p>
              </>
            );
          })()}
        </div>
        <div className="ml-auto text-right text-xs text-white/50">Hover or click a bar to inspect</div>
      </div>

      {/* Chart bars */}
      <div className="flex items-end justify-between gap-1" style={{ height: 150 }}>
        {chartData.map((point, index) => {
          const heightPercent = maxEarnings > 0 ? (point.earnings / maxEarnings) * 100 : 0;
          const hasData = point.earnings > 0;
          return (
            <div
                key={index}
                className="group relative flex-1 cursor-pointer flex items-end h-full"
                onMouseEnter={() => { if (!selectedPoint) setHoveredPoint(point); }}
                onMouseLeave={() => { if (!selectedPoint) setHoveredPoint(null); }}
                onClick={() => setSelectedPoint(selectedPoint && selectedPoint.timestamp.getTime() === point.timestamp.getTime() ? null : point)}
              >
                <div
                  className={`w-full rounded-t-lg transition-opacity group-hover:opacity-80 ${
                    hasData
                      ? "bg-gradient-to-t from-emerald-500 to-lime-400"
                      : "bg-white/10"
                  }`}
                  style={{ height: hasData ? `${Math.max(heightPercent, 2)}%` : "4px", minHeight: hasData ? undefined : 2 }}
                />
              </div>
          );
        })}
      </div>

      {/* X-axis labels (show first, middle, last) */}
      <div className="mt-2 flex justify-between text-xs text-white/50">
        <span>{chartData[0]?.label || ""}</span>
        {chartData.length > 2 && (
          <span>{chartData[Math.floor(chartData.length / 2)]?.label || ""}</span>
        )}
        <span>{chartData[chartData.length - 1]?.label || ""}</span>
      </div>
    </div>
  );
}
